import { useId, useState, type FormEvent } from 'react';
import {
  FACTORES_NIO,
  nivelDesdeTotalNio,
  totalNio,
  type EvaluacionNioCompleta,
  type EvaluacionNioReducida,
  type EvaluacionNioRequest,
  type TablaNio,
} from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

interface Props {
  propiedadId: string;
  evaluaciones: Array<EvaluacionNioCompleta | EvaluacionNioReducida>;
  tabla: TablaNio | null;
  esDireccion: boolean;
  idioma: Idioma;
  onCambio: () => void;
}

type Puntuaciones = Record<(typeof FACTORES_NIO)[number], number>;

const INICIALES: Puntuaciones = {
  valorActivo: 3,
  complejidadTecnica: 3,
  frecuenciaUso: 3,
  exigenciaCliente: 3,
  exposicionClimatica: 3,
  historialIntervencion: 3,
};

function esCompleta(
  evaluacion: EvaluacionNioCompleta | EvaluacionNioReducida,
): evaluacion is EvaluacionNioCompleta {
  return 'total' in evaluacion;
}

/**
 * Evaluacion operativa.
 *
 * El total y el nivel se calculan en vivo con la MISMA funcion que usa el
 * servidor, importada de los contratos compartidos. La cuota sugerida solo se
 * muestra a Direccion: un manager ve el nivel y nada mas.
 *
 * Una evaluacion validada no se edita nunca. Para cambiarla se crea otra, que la
 * sustituye; la anterior queda en el historial explicando por que se cobro lo
 * que se cobro.
 */
export function PanelNio({
  propiedadId,
  evaluaciones,
  tabla,
  esDireccion,
  idioma,
  onCambio,
}: Props) {
  const idBase = useId();
  const [puntuaciones, setPuntuaciones] = useState<Puntuaciones>(INICIALES);
  const [editando, setEditando] = useState<string | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vigente = evaluaciones.find((e) => e.estado === 'VALIDADA');
  const borrador = evaluaciones.find((e) => e.estado === 'BORRADOR');

  const total = totalNio(puntuaciones);
  const nivel = nivelDesdeTotalNio(total);
  const cuotasPorNivel = tabla
    ? [
        tabla.nivel1Cents,
        tabla.nivel2Cents,
        tabla.nivel3Cents,
        tabla.nivel4Cents,
        tabla.nivel5Cents,
      ]
    : [];
  const cuotaCents = cuotasPorNivel[nivel - 1] ?? null;

  const conError = async (accion: () => Promise<unknown>) => {
    setError(null);
    try {
      await accion();
      onCambio();
    } catch (fallo: unknown) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    }
  };

  const guardar = (evento: FormEvent) => {
    evento.preventDefault();
    const datos: EvaluacionNioRequest = puntuaciones;
    void conError(async () => {
      if (editando) await api.editarEvaluacion(editando, datos);
      else await api.crearEvaluacion(propiedadId, datos);
      setAbierto(false);
      setEditando(null);
      setPuntuaciones(INICIALES);
    });
  };

  const abrirEdicion = (evaluacion: EvaluacionNioCompleta) => {
    setPuntuaciones({
      valorActivo: evaluacion.valorActivo,
      complejidadTecnica: evaluacion.complejidadTecnica,
      frecuenciaUso: evaluacion.frecuenciaUso,
      exigenciaCliente: evaluacion.exigenciaCliente,
      exposicionClimatica: evaluacion.exposicionClimatica,
      historialIntervencion: evaluacion.historialIntervencion,
    });
    setEditando(evaluacion.id);
    setAbierto(true);
  };

  return (
    <section className="tarjeta">
      <h2 title={t(idioma, 'nio.explicacion')}>{t(idioma, 'nio.titulo')}</h2>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <ul>
        {evaluaciones.length === 0 && <li>{t(idioma, 'nio.vacio')}</li>}
        {evaluaciones.map((evaluacion) => (
          <li key={evaluacion.id}>
            <strong>
              {t(idioma, 'propiedades.nio')} {evaluacion.nivel}
            </strong>{' '}
            · {t(idioma, `nio.${evaluacion.estado}`)}
            {esCompleta(evaluacion) && (
              <>
                {' '}
                · {t(idioma, 'nio.total')} {evaluacion.total}
                {evaluacion.cuotaSugerida && (
                  <>
                    {' '}
                    · {t(idioma, 'nio.cuotaSugerida')}{' '}
                    {formatearImporteDesdeCents(evaluacion.cuotaSugerida.cents, idioma, {
                      moneda: evaluacion.cuotaSugerida.moneda,
                    })}
                  </>
                )}
              </>
            )}
            {evaluacion.estado === 'BORRADOR' && (
              <>
                {' '}
                {esCompleta(evaluacion) && (
                  <button type="button" className="enlace" onClick={() => abrirEdicion(evaluacion)}>
                    {t(idioma, 'nio.editar')}
                  </button>
                )}
                {esDireccion && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => void conError(() => api.validarEvaluacion(evaluacion.id))}
                    >
                      {t(idioma, 'nio.validar')}
                    </button>
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      {!abierto && (
        <button type="button" className="secundario" onClick={() => setAbierto(true)}>
          {vigente ? t(idioma, 'nio.sustituir') : t(idioma, 'nio.nueva')}
        </button>
      )}

      {abierto && (
        <form className="formulario" onSubmit={guardar}>
          {borrador && !editando && <p className="error">{t(idioma, 'nio.yaHayBorrador')}</p>}

          {FACTORES_NIO.map((factor) => (
            <div className="campo" key={factor}>
              <label htmlFor={`${idBase}-${factor}`}>{t(idioma, `nio.factor.${factor}`)}</label>
              <input
                id={`${idBase}-${factor}`}
                type="number"
                min={1}
                max={5}
                step={1}
                required
                value={puntuaciones[factor]}
                onChange={(e) =>
                  setPuntuaciones({ ...puntuaciones, [factor]: Number(e.target.value) })
                }
              />
            </div>
          ))}

          {/* Vista previa en vivo. La cuota, solo para Direccion. */}
          <p>
            <strong>
              {t(idioma, 'nio.total')}: {total} · {t(idioma, 'propiedades.nio')} {nivel}
            </strong>
            {esDireccion && cuotaCents !== null && (
              <>
                {' '}
                · {t(idioma, 'nio.cuotaSugerida')}:{' '}
                {formatearImporteDesdeCents(cuotaCents, idioma, { moneda: tabla?.moneda ?? 'EUR' })}
              </>
            )}
          </p>

          <div className="acciones">
            <button type="submit">{t(idioma, 'app.guardar')}</button>
            <button
              type="button"
              className="secundario"
              onClick={() => {
                setAbierto(false);
                setEditando(null);
              }}
            >
              {t(idioma, 'app.cancelar')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
